import type { FormEvent, ReactNode } from 'react';
import { Box, Button, Spinner } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import './EducationProfile.scss';

type EducationProfileProps = {
  isLoading: boolean;
  isError: boolean;
  errorMessage: string;
  summary: ReactNode;
  title?: string;
  skuLabel: string;
  sku: string;
  statusLabel: string;
  status: ReactNode;
  statusNote?: ReactNode;
  headerActions?: ReactNode;
  publicProfile?: ReactNode;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  children: ReactNode;
  beforeActions?: ReactNode;
  onCancel: () => void;
  cancelDisabled: boolean;
  isSaving: boolean;
  submitDisabled: boolean;
  extraActions?: ReactNode;
};

export function EducationProfile({
  isLoading,
  isError,
  errorMessage,
  summary,
  title,
  skuLabel,
  sku,
  statusLabel,
  status,
  statusNote,
  headerActions,
  publicProfile,
  onSubmit,
  children,
  beforeActions,
  onCancel,
  cancelDisabled,
  isSaving,
  submitDisabled,
  extraActions,
}: EducationProfileProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="education-profile-layout">
        <Box className="education-profile-page" py={8}>
          <Spinner />
        </Box>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="education-profile-layout">
        <Box className="education-profile-page">
          <p className="description">{errorMessage}</p>
        </Box>
      </div>
    );
  }

  return (
    <div className="education-profile-layout">
      <aside className="education-profile-layout__summary">{summary}</aside>

      <div className="education-profile-layout__main">
        <div className="education-profile-page">
          <header className="education-profile-page__header">
            <div className="education-profile-page__intro">
              {title ? (
                <div className="education-profile-page__heading">
                  <h1>{title}</h1>
                </div>
              ) : null}
              <div className="education-profile-page__meta">
                <div className="education-profile-page__meta-item">
                  <span className="education-profile-page__meta-label">
                    {skuLabel}
                  </span>
                  <span className="education-profile-page__meta-value">{sku}</span>
                </div>
                <div className="education-profile-page__meta-item">
                  <span className="education-profile-page__meta-label">
                    {statusLabel}
                  </span>
                  {status}
                </div>
              </div>
              {statusNote}
            </div>
            {headerActions ? (
              <div className="education-profile-page__preview">{headerActions}</div>
            ) : null}
          </header>

          <div className="education-profile-page__divider" />

          {publicProfile}

          <form className="education-profile-form" onSubmit={onSubmit}>
            {children}
            <div className="education-profile-form__divider" />
            {beforeActions}
            <div className="education-profile-form__actions">
              <Button
                type="button"
                variant="outline"
                className="button--secondary"
                onClick={onCancel}
                isDisabled={cancelDisabled}
              >
                {t('cancel')}
              </Button>
              <div className="education-profile-form__actions-primary">
                <Button
                  type="submit"
                  isLoading={isSaving}
                  isDisabled={submitDisabled}
                  colorScheme="blue"
                  className="button--primary"
                >
                  {t('common.save')}
                </Button>
                {extraActions}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
